import { useState, useMemo, type HTMLAttributes } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { SingleItemAutocomplete } from '~components/autocomplete/single-item-autocomplete'
import {
  type Q88ListResItem,
  useGetQ88List,
} from '~hooks/queries/q88/use-get-q88-list'
import { inputWidth } from '../../add-visit-flow'

const MIN_CHARS_FOR_SEARCH = 3

interface VesselOption {
  label: string
  vessel: Q88ListResItem
}

function buildVesselLabel(vessel: Q88ListResItem): string {
  if (vessel.VesselName && vessel.IMONumber) {
    return `${vessel.VesselName} (${vessel.IMONumber})`
  }
  return vessel.VesselName || vessel.IMONumber || ''
}

interface VesselAutocompleteProps {
  vesselName?: string
  imoNumber?: string
  onVesselChange: (
    vesselName: string | undefined,
    imoNumber: string | undefined,
  ) => void
  error?: boolean
}

export function VesselAutocomplete({
  vesselName,
  imoNumber,
  onVesselChange,
  error = false,
}: VesselAutocompleteProps) {
  const [inputValue, setInputValue] = useState(vesselName || '')

  const shouldFetch = !vesselName && inputValue.length >= MIN_CHARS_FOR_SEARCH

  const { data: q88List, isLoading } = useGetQ88List({
    vesselNameAndImo: shouldFetch ? inputValue : undefined,
    disabled: !shouldFetch,
  })

  const options: VesselOption[] = useMemo(() => {
    if (!q88List) return []
    return (q88List as Q88ListResItem[])
      .filter((item) => item.VesselName)
      .map((vessel) => ({
        label: buildVesselLabel(vessel),
        vessel,
      }))
  }, [q88List])

  const selectedOption = useMemo(() => {
    if (!vesselName) return null
    const matchedOption = options.find(
      (opt) =>
        opt.vessel.VesselName === vesselName ||
        opt.vessel.IMONumber === imoNumber,
    )
    if (matchedOption) return matchedOption
    return {
      label: imoNumber ? `${vesselName} (${imoNumber})` : vesselName,
      vessel: {
        VesselName: vesselName,
        IMONumber: imoNumber || '',
      } as Q88ListResItem,
    }
  }, [vesselName, imoNumber, options])

  return (
    <SingleItemAutocomplete<VesselOption>
      sx={{ width: inputWidth }}
      label="Vessel *"
      input={selectedOption?.label || ''}
      setInputValue={setInputValue}
      options={options}
      placeholder="Type 3 characters to search"
      hasError={error}
      getOptionLabel={(option: VesselOption | string) =>
        typeof option === 'string' ? option : option.label
      }
      renderOption={(
        props: HTMLAttributes<HTMLLIElement> & { key: string },
        option: VesselOption,
      ) => {
        const { key, ...otherProps } = props
        return (
          <Box component="li" key={key} {...otherProps}>
            <Typography variant="paragraphBold1">
              {option.vessel.VesselName}
              {option.vessel.IMONumber && (
                <Typography component="span" variant="paragraph2">
                  {' '}
                  ({option.vessel.IMONumber})
                </Typography>
              )}
            </Typography>
          </Box>
        )
      }}
      isOptionEqualToValue={(
        option: VesselOption,
        val: VesselOption | string,
      ) => {
        if (typeof val === 'string') return option.label === val
        return (
          option.vessel.IMONumber === val.vessel.IMONumber &&
          option.vessel.VesselName === val.vessel.VesselName
        )
      }}
      onChangeHandler={(_e, newValue) => {
        if (!newValue || typeof newValue === 'string') {
          onVesselChange(undefined, undefined)
          return
        }
        onVesselChange(newValue.vessel.VesselName, newValue.vessel.IMONumber)
      }}
      filterOptions={(x: VesselOption[]) => x}
      endAdornment={isLoading ? <CircularProgress size={20} /> : null}
    />
  )
}
